import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import AppBar from '@groovehq/internal-design-system/lib/components/AppBar/AppBar'
import useExpiryEvent from 'subapps/chat/hooks/useExpiryEvent'
import { selectRoomFromUrlBridgeType } from 'ducks/chat/selectors/rooms'
import styles from './styles.less'

const timeLabel = (mins, text) => {
  const rounded = Math.ceil(mins)
  return `${rounded} ${text}${rounded > 1 ? 's' : ''}`
}

// NOTE (jscheel): Ideally all of this, including times, would live in
// configuration, but not going to spend the time refactoring that now.
const serviceLabel = bridgeType => {
  switch (bridgeType) {
    case 'g.bridge.messenger.facebook':
      return 'Messenger'
    case 'g.bridge.messenger.instagram':
      return 'Instagram'
    default:
      return 'Groove'
  }
}

const ExpirationStatusBarView = () => {
  const { isExpirable, remainingMins } = useExpiryEvent()
  const bridgeType = useSelector(selectRoomFromUrlBridgeType)
  const prevRemainingMinsRef = useRef()
  const [showAnimation, setShowAnimation] = useState(false)

  const expiryMessage = useMemo(
    () => {
      const service = serviceLabel(bridgeType)

      if (remainingMins > 24 * 60) {
        return {
          type: 'positive',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins / 60 / 24,
            'day'
          )}`,
          description: `${service} automatically closes conversations with no new replies within 7 days.`,
        }
      }

      if (remainingMins > 1 * 60) {
        return {
          type: 'warning',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins / 60,
            'hour'
          )}`,
          description: `${service} automatically closes conversations with no initial response time within 24 hours.`,
        }
      }

      if (remainingMins > 1) {
        return {
          type: 'negative',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins,
            'minute'
          )}`,
          description: `${service} automatically closes conversations with no initial response time within 24 hours.`,
        }
      }

      return {
        type: 'negative',
        title: `Conversation closed by ${service}`,
        description: `${service} automatically closes conversations with no messages within 7 days.`,
      }
    },
    [remainingMins, bridgeType]
  )

  useEffect(
    () => {
      let animationTimer

      if (
        prevRemainingMinsRef?.current &&
        prevRemainingMinsRef.current !== remainingMins
      ) {
        setShowAnimation(true)
        animationTimer = setTimeout(() => setShowAnimation(false), 2000)
      }

      prevRemainingMinsRef.current = remainingMins

      return () => {
        if (animationTimer) clearTimeout(animationTimer)
      }
    },
    [remainingMins]
  )

  if (!isExpirable) return null

  return (
    <AppBar
      type={expiryMessage.type}
      closable={false}
      close={false}
      className={cn(styles.wrapper, styles[expiryMessage.type], {
        appBarFadeOut: showAnimation,
        appBarFadeIn: !showAnimation,
      })}
    >
      <span className={styles.title}>{expiryMessage.title}</span>
      <br />
      <span>{expiryMessage.description}</span>{' '}
      <a
        className={styles.link}
        href="https://help.groovehq.com/help/messenger-reply-times"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more.
      </a>
    </AppBar>
  )
}

export default ExpirationStatusBarView
